/**
 * Los iconos de las tarjetas del resumen.
 *
 * Trazo, no relleno, y todos con la misma rejilla de 24 y el mismo grosor: puestos en fila tienen
 * que leerse como una familia, no como seis dibujos distintos. Heredan el color con `currentColor`,
 * asi que una tarjeta en rojo se pone roja entera sin tocar el SVG.
 *
 * No compiten con la cifra: van en el color tenue. El unico acento de la pantalla es el naranja de
 * lo que se puede tocar, y un icono no se toca.
 */

type P = { size?: number };

const base = (size: number) => ({
  width: size,
  height: size,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.6,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
  focusable: false as const,
});

/** Capital: monedas apiladas. Lo que hay puesto. */
export const IcoCapital = ({ size = 17 }: P) => (
  <svg {...base(size)}>
    <ellipse cx="12" cy="6.5" rx="7" ry="3" />
    <path d="M5 6.5v5c0 1.7 3.1 3 7 3s7-1.3 7-3v-5" />
    <path d="M5 11.5v5c0 1.7 3.1 3 7 3s7-1.3 7-3v-5" />
  </svg>
);

/** Objetivo: una diana. Cuánto falta para volver a lo que pusiste. */
export const IcoObjetivo = ({ size = 17 }: P) => (
  <svg {...base(size)}>
    <circle cx="12" cy="12" r="8" />
    <circle cx="12" cy="12" r="4" />
    <circle cx="12" cy="12" r="1" fill="currentColor" stroke="none" />
  </svg>
);

/** Ganado: la línea que sube. */
export const IcoGanado = ({ size = 17 }: P) => (
  <svg {...base(size)}>
    <path d="M3 17l5-5 3.5 3.5L21 7" />
    <path d="M15 7h6v6" />
  </svg>
);

/** Proyección: la tendencia a este ritmo — eje y la línea que se prolonga a futuro (a trazos). */
export const IcoProyeccion = ({ size = 17 }: P) => (
  <svg {...base(size)}>
    <path d="M4 4v16h16" />
    <path d="M7 15l4-4 3 2" />
    <path d="M14 13l5-5" strokeDasharray="3 2.5" />
    <circle cx="19" cy="8" r="1.3" fill="currentColor" stroke="none" />
  </svg>
);

/** Retirable: sale del contrato hacia ti. */
export const IcoRetirar = ({ size = 17 }: P) => (
  <svg {...base(size)}>
    <path d="M12 3v11" />
    <path d="M8 10.5l4 4 4-4" />
    <path d="M4 16v3a2 2 0 002 2h12a2 2 0 002-2v-3" />
  </svg>
);

/** Ya retirado: lo que salió y llegó. */
export const IcoRetirado = ({ size = 17 }: P) => (
  <svg {...base(size)}>
    <path d="M3 8.5A2.5 2.5 0 015.5 6h13A2.5 2.5 0 0121 8.5v9a2.5 2.5 0 01-2.5 2.5h-13A2.5 2.5 0 013 17.5z" />
    <path d="M16 13h2" />
    <path d="M7 4l5 3 5-3" />
  </svg>
);

/** Bolsa de gas: la gota que paga los reajustes. */
export const IcoGas = ({ size = 17 }: P) => (
  <svg {...base(size)}>
    <path d="M12 3.5s5.5 5.2 5.5 9a5.5 5.5 0 11-11 0c0-3.8 5.5-9 5.5-9z" />
    <path d="M9.5 14.5a2.5 2.5 0 002.5 2.5" />
  </svg>
);

/** Reajustes que quedan: el ciclo, con su contador. */
export const IcoReajustes = ({ size = 17 }: P) => (
  <svg {...base(size)}>
    <path d="M20 12a8 8 0 01-13.7 5.6M4 12a8 8 0 0113.7-5.6" />
    <path d="M17.7 3.5v3h-3" />
    <path d="M6.3 20.5v-3h3" />
  </svg>
);

/** Reserva (DCA): lo apartado, que entra por tramos. */
export const IcoReserva = ({ size = 17 }: P) => (
  <svg {...base(size)}>
    <path d="M12 3l8 4.5-8 4.5-8-4.5z" />
    <path d="M4 12l8 4.5 8-4.5" />
    <path d="M4 16.5L12 21l8-4.5" />
  </svg>
);

/** Añadir fondos: entra dinero. */
export const IcoAnadir = ({ size = 16 }: P) => (
  <svg {...base(size)}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 8.5v7M8.5 12h7" />
  </svg>
);

/** Quitar liquidez: sale una parte, no todo. */
export const IcoQuitar = ({ size = 16 }: P) => (
  <svg {...base(size)}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M8.5 12h7" />
  </svg>
);

/** Sacar todo: la puerta. Es la única acción de la que no se vuelve. */
export const IcoSalir = ({ size = 16 }: P) => (
  <svg {...base(size)}>
    <path d="M10 20H6a2 2 0 01-2-2V6a2 2 0 012-2h4" />
    <path d="M15.5 8.5L19 12l-3.5 3.5" />
    <path d="M19 12H10" />
  </svg>
);

/** Compartir: los tres nodos unidos, el símbolo universal. */
export const IcoCompartir = ({ size = 15 }: P) => (
  <svg {...base(size)}>
    <circle cx="18" cy="5.5" r="2.6" />
    <circle cx="6" cy="12" r="2.6" />
    <circle cx="18" cy="18.5" r="2.6" />
    <path d="M8.3 10.8l7.4-3.9M8.3 13.2l7.4 3.9" />
  </svg>
);

/** Tiempo: un reloj. Cuánto lleva la posición trabajando sin parar. */
export const IcoTiempo = ({ size = 17 }: P) => (
  <svg {...base(size)}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 7.5V12l3 2" />
  </svg>
);

/** El enlace a la posición dentro del DEX. */
export const IcoPosicion = ({ size = 15 }: P) => (
  <svg {...base(size)}>
    <rect x="3" y="4" width="18" height="16" rx="2.5" />
    <path d="M3 9.5h18" />
    <path d="M8 14h5" />
  </svg>
);

/* ------------------------------------------------------------------ navegacion de la barra
   Los tres de arriba eran emojis (🌉 🤝 🏛️). Un emoji lo dibuja el sistema operativo: cambia de
   forma, de color y de peso en cada maquina, no hereda el color del texto y no hay manera de que
   case con esta familia de trazo. Aqui van con la misma rejilla y el mismo grosor que el resto. */

/** Puente: lo que va y lo que viene entre dos cadenas.
 *
 *  EL PRIMER INTENTO ERA UN PUENTE COLGANTE —arco, pilares y tirantes— y a 16 px se leia como un
 *  peine: los tirantes quedaban a menos de un pixel de separacion y el arco desaparecia. El dibujo
 *  literal no siempre es el legible. Dos flechas opuestas dicen lo mismo y sobreviven al tamaño. */
export const IcoPuente = ({ size = 16 }: P) => (
  <svg {...base(size)}>
    <path d="M4 9h16" />
    <path d="M17 6l3 3-3 3" />
    <path d="M20 15H4" />
    <path d="M7 12l-3 3 3 3" />
  </svg>
);

/** Referidos: alguien que trae a alguien. */
export const IcoReferidos = ({ size = 16 }: P) => (
  <svg {...base(size)}>
    <circle cx="9" cy="8" r="3.2" />
    <path d="M3.5 19a5.5 5.5 0 0 1 11 0" />
    <path d="M17 8.5v5" />
    <path d="M14.5 11h5" />
  </svg>
);

/** Gobierno: el frontispicio clasico, que es lo que ya sugeria el emoji que sustituye. */
export const IcoGobierno = ({ size = 16 }: P) => (
  <svg {...base(size)}>
    <path d="M3.5 9.5 12 5l8.5 4.5" />
    <path d="M6 11v6" />
    <path d="M10 11v6" />
    <path d="M14 11v6" />
    <path d="M18 11v6" />
    <path d="M4 19.5h16" />
  </svg>
);

/** La punta del desplegable. Se dibuja aqui en vez de escribir «▾»: ese caracter se apoya en la
 *  linea base de la fuente, asi que queda descolgado respecto al texto que acompaña, y ademas
 *  cambia de forma segun la tipografia que resuelva el sistema. */
export const IcoFlecha = ({ size = 12 }: P) => (
  <svg {...base(size)}>
    <path d="M6 9.5l6 6 6-6" />
  </svg>
);

/* Glifos del timeline del historial: una familia por tipo de evento. */

/** Tu dinero: moneda con el signo. */
export const IcoDinero = ({ size = 14 }: P) => (
  <svg {...base(size)}>
    <circle cx="12" cy="12" r="9" />
    <path d="M14.6 8.6a3 3 0 0 0-2.6-1.4c-1.5 0-2.7.9-2.7 2.1 0 2.9 5.4 1.7 5.4 4.7 0 1.2-1.2 2.1-2.7 2.1a3 3 0 0 1-2.6-1.4" />
    <path d="M12 5.5v13" />
  </svg>
);

/** El robot: el pulso de una operación. */
export const IcoRobot = ({ size = 14 }: P) => (
  <svg {...base(size)}>
    <path d="M3 12h4l3-8 4 16 3-8h4" />
  </svg>
);

/** Ajustes del dueño: mandos. */
export const IcoAjuste = ({ size = 14 }: P) => (
  <svg {...base(size)}>
    <path d="M4 21v-6M4 11V3M12 21v-8M12 9V3M20 21v-4M20 13V3" />
    <path d="M2 15h4M10 9h4M18 17h4" />
  </svg>
);

/** Aviso: el triángulo. */
export const IcoAviso = ({ size = 14 }: P) => (
  <svg {...base(size)}>
    <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h16.9a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
    <path d="M12 9v4M12 16.5h.01" />
  </svg>
);
